import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Breadcrumb, Col, Collapse, Row, Space, Table } from "antd";

import {
    ArrowRight,
    Bank,
    ChevronRight,
    Clock,
    Document,
    MoneyBagCoin,
    User,
} from "assets/icons";

import Spacer from "components/Spacer";
import Container from "components/Container";
import { useSingle } from "modules/serviceRegister/hooks";

import cls from "./ServiceView.module.scss";
import MetaDataCard from "pages/ServiceRegister/components/MetaDataCard";

import davXizmat from "../../assets/images/davxizmat.png";
import { Link } from "react-router-dom";
import config from "config";

const ServicePoint = ({ imgSrc, title }) => {
    return (
        <div className={cls.servicePoint}>
            <img src={imgSrc} alt="service point" />
            <span>{title}</span>
            <ChevronRight />
        </div>
    );
};

function getMockData(id) {
    switch (id) {
        case "9d2fc7e5-b302-4092-9815-8dc9b793f453":
            return {
                step: 6,
                field: 46
            }
        case "f5305aaa-0fca-4b88-97bf-9265b77314fe":
            return {
                step: 7,
                field: 44
            }
        case "838e8248-0a12-4e92-8d2c-6637522a959a":
            return {
                step: 7,
                field: 52
            }
        case "1c8377b2-d5b6-41fd-b3e8-dd9d93cc093a":
            return {
                step: 6,
                field: 65
            }
        case "9b99aceb-209e-4b99-bba6-ae8fd8832dbb":
            return {
                step: 6,
                field: 55
            }
        default:
            return {
                step: 0,
                field: 0
            }
    }
}

const ServiceView = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();

    const { item, isFetched } = useSingle({ id });

    const items = [
        {
            key: "1",
            label: t("service_approval_authorities"),
            children: item?.organizations.length > 0 ? <ul>{item?.organizations?.map(value => <li>{value.name[i18n.language]}</li>)}</ul> : <p className={cls.slash}>—</p>,
        },
        {
            key: "2",
            label: t("service_regulatory_documents"),
            children: item?.principles.length > 0 ? <ol>{item?.principles?.map(value => <li><span>{value.name}</span> <a href={value.link}>{value.link}</a></li>)}</ol> : <p className={cls.slash}>—</p>,
        },
        {
            key: "3",
            label: t("service_delivery_point"),
            children: item?.servicePoints.length > 0 ? <>
                <ul style={{ listStyleType: 'none' }}>{item?.servicePoints.map(is => <li>
                    {is?.file?.uuid && <img src={`${config.api.img_url}/${is?.file?.uuid}`} alt="logo" />}
                    <a href={`${is.link.startsWith("https") ? is.link : `https://${is.link}`}`}
                        target="_blank">
                        {is.name}
                    </a>
                </li>)}
                </ul>
            </> : <p className={cls.slash}>—</p>,
        },
        {
            key: "4",
            label: t("service_required_documents"),
            children: item?.documents.length > 0 ? <ol>{item?.documents?.map(value => <li>{value}</li>)}</ol> : <p className={cls.slash}>—</p>,
        },
        {
            key: "5",
            label: t("service_payment_details"),
            children: <div>
                {
                    item?.payments.length > 0 ? item.payments?.map(value => <div key={value.id} className={cls.item}>
                        <p>{t(value.type)}:</p>
                        <p>{value.value.toLocaleString('ru-RU')}</p>
                        <p>{value.accelerated ? t("field_payments_accelerated") : ""}</p>
                    </div>) : <p className={cls.slash}>—</p>
                }
            </div>,
        },
        {
            key: "6",
            label: t("service_requirements"),
            children: item?.requirements.length > 0 ? <ul>{item?.requirements?.map(value => <li key={value.id}>{value}</li>)}</ul> : <p className={cls.slash}>—</p>,
        },
        {
            key: "7",
            label: t("service_life_situations"),
            children: item?.lifeSituations.length > 0 ? <ul>{item?.lifeSituations?.map(ls => <li key={ls.id}>
                <h4>{ls.name[i18n.language]}</h4>
                <p>{ls.description[i18n.language]}</p>
            </li>)}</ul> : <p className={cls.slash}>—</p>,
        },
        {
            key: "8",
            label: t("service_comment"),
            children: item?.description ? item.description : <p className={cls.slash}>—</p>
        },
    ];

    return (
        <Container>
            <Spacer size={35} />

            <Breadcrumb separator=">">
                <Breadcrumb.Item href="/">{t("home")}</Breadcrumb.Item>
                <Breadcrumb.Item href="/service-register">{t("service_register")}</Breadcrumb.Item>
            </Breadcrumb>

            <Spacer size={32} />

            <div className={cls.content}>
                <div className={cls.flex}>
                    <div className={cls.left}>
                        <strong className={cls.largeTitle}>
                            {item.serviceName ? item.serviceName[i18n.language] : ''}
                        </strong>

                        {item?.addition?.length > 0 && (
                            <div className={cls.additionBlock}>
                                <span className={cls.additionTitle}>
                                    {t("service_subtype_name")}
                                </span>

                                <div className={cls.additionList}>
                                    {item.addition.map((value, index) => (
                                        <div key={index} className={cls.additionItem}>
                                            <span className={cls.arrow}>↳</span>
                                            <span className={cls.additionText}>
                                                {value[i18n.language]}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={cls.right}>
                        <span className={cls.title}>{t("chapter_number")}</span>
                        <strong className={cls.largeTitle}>{item.uniqueNumber}</strong>
                    </div>
                </div>
            </div>

            <div className={cls.center}>
                <Row gutter={[8, 8]} align={'stretch'}>
                    <Col xs={24} sm={12} lg={6}>
                        <MetaDataCard
                            icon={<Clock />}
                            title={t("application_review_period")}
                            text={item?.expire.length > 0 && (
                                <ul>
                                    {item.expire.map(value => <li key={value.id}>{`${value.value ? value.value : ""} ${t(value.type)} ${value.accelerated ? `- ${t("field_payments_accelerated")}` : ""}`}</li>)}
                                </ul>
                            )}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <MetaDataCard
                            icon={<Document />}
                            title={t("service_result")}
                            text={item?.serviceResult}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <MetaDataCard
                            icon={<User />}
                            title={t("applicant_category")}
                            text={t(item?.subjectType)}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <MetaDataCard
                            icon={<MoneyBagCoin />}
                            title={t("service_price")}
                            text={t(item?.paymentType)}
                        />
                    </Col>
                </Row>

                <Spacer size={32} />

                <Row gutter={[24]}>
                    <Col xs={24} sm={12} lg={16}>
                        <Collapse size="large" items={items} bordered={false} />
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
                            <MetaDataCard
                                icon={<Bank />}
                                title={t("authorized_body")}
                                text={item?.organization?.name[i18n.language]}
                                background="grey"
                            />
                            <Space size={16} />
                            <MetaDataCard
                                icon={<Clock />}
                                title={t("result_validity_period")}
                                text={`${item?.validityPeriod?.value} ${t(item?.validityPeriod?.type)}`}
                                background="grey"
                            />
                            {item?.serviceType && (
                                <MetaDataCard
                                    icon={<Document />}
                                    title={t("service_type")}
                                    text={t(item?.serviceType)}
                                    background="grey"
                                />
                            )}
                            <MetaDataCard
                                icon={<Document />}
                                title={t("service_step_and_field")}
                                text={
                                    <>
                                        <p>{t("step")}: {item?.serviceSteps?.length} ta</p>
                                        <p>{t("field")}: {item?.documentTypeIds?.length} ta</p>
                                    </>
                                }
                                background="grey"
                            />
                            {item?.documentTypeIds?.length > 0 && (
                                <MetaDataCard
                                    icon={<Document />}
                                    title={t("document_types")}
                                    text={
                                        <>
                                            <ol style={{ paddingLeft: "20px" }}>
                                                {item?.documentTypeIds?.map((docType) => (
                                                    <li key={docType?.id}>{docType.name[i18n.language]}</li>
                                                ))}
                                            </ol>
                                            {item?.documentTypeDescription &&
                                                (
                                                    <div>
                                                        <p>{t("field_document_type_description")}</p>
                                                        <h4>{item?.documentTypeDescription}</h4>
                                                    </div>
                                                )
                                            }
                                        </>
                                    }
                                    background="grey"
                                />
                            )}
                        </Space>
                    </Col>
                </Row>

                {item?.serviceType == "COMPOSITE" && item?.compositeServices?.length > 0 && (
                    <>
                        <Spacer size={40} />
                        <h2 className={cls.titleSecondary}>{t("composite_services")}</h2>
                        <Spacer size={24} />
                        <Row gutter={[16, 16]}>
                            {item?.compositeServices?.map((service, index) => (
                                <Col xs={24} sm={12} lg={6} key={index}>
                                    <div style={{ padding: "16px", background: "#fff", borderRadius: "16px", boxShadow: "0 4px 16px rgba(4, 22, 116, 0.06)" }}>
                                        <p className={cls.cardTitle} title={service.name[i18n.language]}>
                                            {service.name[i18n.language]}
                                        </p>
                                        <Link to={`/service-register/view/${service.id}`} style={{ display: "flex", alignItems: "center", gap: "12px", color: "#000C7499" }}>
                                            <span style={{ fontSize: "16px" }}>{t("details")}</span>
                                            <ArrowRight />
                                        </Link>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}

                {item?.services?.length > 0 && (
                    <>
                        <Spacer size={40} />
                        <h2 className={cls.titleSecondary}>{t("related_state_services")}</h2>
                        <Spacer size={24} />
                        <Row gutter={[16, 16]}>
                            {item?.services?.map((service, index) => (
                                <Col xs={24} sm={12} lg={6} key={index}>
                                    <div style={{ padding: "16px", background: "#fff", borderRadius: "16px", boxShadow: "0 4px 16px rgba(4, 22, 116, 0.06)" }}>
                                        <p className={cls.cardTitle}>{service.name[i18n.language]}</p>
                                        <Link to={`/service-register/view/${service.id}`} style={{ display: "flex", alignItems: "center", gap: "12px", color: "#000C7499" }}>
                                            <span style={{ fontSize: "16px" }}>{t("details")}</span>
                                            <ArrowRight />
                                        </Link>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </div>
        </Container>

    );
};

export default ServiceView;
